/**
 * System Design Canvas Storage
 * IndexedDB persistence for diagrams
 */

import type { SystemDesignDiagram, DiagramSettings } from './types';
import { DEFAULT_DIAGRAM_SETTINGS } from './types';

const DB_NAME = 'system-design-db';
const DB_VERSION = 1;
const STORE_NAME = 'diagrams';

/**
 * Initialize the database
 */
export async function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('name', 'name', { unique: false });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
  });
}

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a new diagram
 */
export async function createDiagram(name: string): Promise<SystemDesignDiagram> {
  const db = await initDB();
  const now = new Date().toISOString();

  const diagram: SystemDesignDiagram = {
    id: generateId(),
    name,
    components: [],
    connections: [],
    annotations: [],
    settings: { ...DEFAULT_DIAGRAM_SETTINGS },
    createdAt: now,
    updatedAt: now,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.add(diagram);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(diagram);
  });
}

/**
 * Save/update a diagram
 */
export async function saveDiagram(diagram: SystemDesignDiagram): Promise<void> {
  const db = await initDB();
  const updatedDiagram = {
    ...diagram,
    updatedAt: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(updatedDiagram);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Load a diagram by ID
 */
export async function loadDiagram(id: string): Promise<SystemDesignDiagram | null> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || null);
  });
}

/**
 * List all diagrams
 */
export async function listDiagrams(): Promise<SystemDesignDiagram[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('updatedAt');
    const request = index.openCursor(null, 'prev');
    const diagrams: SystemDesignDiagram[] = [];

    request.onerror = () => reject(request.error);
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        diagrams.push(cursor.value);
        cursor.continue();
      } else {
        resolve(diagrams);
      }
    };
  });
}

/**
 * Delete a diagram
 */
export async function deleteDiagram(id: string): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Duplicate a diagram
 */
export async function duplicateDiagram(id: string, newName: string): Promise<SystemDesignDiagram> {
  const original = await loadDiagram(id);
  if (!original) {
    throw new Error('Diagram not found');
  }

  const now = new Date().toISOString();
  const duplicated: SystemDesignDiagram = {
    ...original,
    id: generateId(),
    name: newName,
    createdAt: now,
    updatedAt: now,
  };

  await saveDiagram(duplicated);
  return duplicated;
}

/**
 * Export diagram to JSON
 */
export function exportToJSON(diagram: SystemDesignDiagram): string {
  return JSON.stringify(diagram, null, 2);
}

/**
 * Import diagram from JSON
 */
export async function importFromJSON(json: string): Promise<SystemDesignDiagram> {
  const parsed = JSON.parse(json) as SystemDesignDiagram;
  const now = new Date().toISOString();

  const diagram: SystemDesignDiagram = {
    ...parsed,
    id: generateId(),
    createdAt: now,
    updatedAt: now,
  };

  await saveDiagram(diagram);
  return diagram;
}

/**
 * Calculate total cost for a diagram
 */
export function calculateTotalCost(diagram: SystemDesignDiagram): number {
  return diagram.components.reduce((total, component) => {
    if (component.cost?.monthlyCost) {
      const replicas = component.scaling?.replicas || 1;
      return total + (component.cost.monthlyCost * replicas);
    }
    return total;
  }, 0);
}
